import React, { useEffect, useMemo, useState } from 'react';
import {
    getMonitorBadgeLabel,
    getMonitorDayUseSplitBadgePlan,
    COMPACT_MONITOR_DOT_PADDING_TOP_PX,
    MONITOR_BADGE_DAY_USE_GRADIENT_BG
} from '../../../services/monitorBadgeDisplay';
import MonitorCartInspectorFooterRow from './MonitorCartInspectorFooterRow';
import MiniProgressBar from '../../components/Mode/taskProcess/MiniProgressBar';
import {
    buildMonitorDoorCardLayoutState,
    COMPACT_MONITOR_CELL_MARGIN_BOTTOM_PX
} from './monitorDoorCardModel';

/**
 * Mirrors MontiorCard status-bar colors so compact circles match the door “status box”
 * (outer border, inner ring, fill, foreground).
 */
export function getCompactCirclePalette(data) {
    const isDnd =
        data?.isDnd ||
        data?.inspection_state?.toLowerCase?.() === 'dnd' ||
        data?.status?.toLowerCase?.() === 'dnd';

    if (isDnd) {
        return {
            outerBorder: '#000000',
            ringColor: '#ffffff',
            innerFill: '#1a1a1a',
            roomNumberColor: '#ffffff'
        };
    }

    if (data?.service_status === 'out_of_service' || data?.service_status === 'out_of_order') {
        return {
            outerBorder: '#dc3545',
            ringColor: '#ffffff',
            innerFill: '#dc3545',
            roomNumberColor: '#ffffff'
        };
    }

    if (data?.status_display_text) {
        return {
            outerBorder: data.status_display_outer_border_color || '#000000',
            ringColor: data.status_display_inner_border_color || '#ffffff',
            innerFill: data.status_display_backcolor || '#ffffff',
            roomNumberColor: data.status_display_forecolor || '#06A9F4'
        };
    }

    return {
        outerBorder: '#000000',
        ringColor: '#ffffff',
        innerFill: data?.statusColor || '#cccccc',
        roomNumberColor: '#ffffff'
    };
}

function sortConfirmationBadges(list) {
    return [...list].sort((a, b) => {
        const aCheckin = a.checkin_today ? 1 : 0;
        const aCheckout = a.checkout_today ? 1 : 0;
        const bCheckin = b.checkin_today ? 1 : 0;
        const bCheckout = b.checkout_today ? 1 : 0;
        if (aCheckin !== bCheckin) return bCheckin - aCheckin;
        if (aCheckout !== bCheckout) return bCheckout - aCheckout;
        return 0;
    });
}

function dotColor(conf) {
    if (conf.checkout_today) return '#dc3545';
    if (conf.checkin_today) return '#28a745';
    return '#06A9F4';
}

/** Total diameter including outer border (25% smaller than original 56px baseline → 42px). */
const OUTER_DIAMETER = 42;

/** Reservation indicator dots above compact room circles (50% larger than original 8px). */
const COMPACT_RESERVATION_DOT_SIZE = 12;

/**
 * Compact floor monitor: circular room with status colors + small colored dots for reservation badges.
 * Uses nested rings so outer status border and inner “stripe” border both read clearly (see MontiorCard status strip).
 */
const CompactRoomMonitorCircle = ({
    data,
    roomNumber,
    onBadgeClick,
    /** Optional: e.g. toggle door preview on touch / devices without hover */
    onCircleClick,
    /** Optional: attach to the circle for Popper so the door preview hugs the ring (not the whole cell) */
    popperTargetRef,
    badgeDisplayMode,
    /** Align cart/inspector band to tallest below-shell row on the floor (same as door cards). */
    footerSlotHeightPx = null
}) => {
    const palette = useMemo(() => getCompactCirclePalette(data), [data]);
    const [blink, setBlink] = useState(true);

    const layout = useMemo(
        () =>
            buildMonitorDoorCardLayoutState({
                data,
                isDnd: data?.isDnd,
                status: data?.status,
                show_icon: data?.show_icon,
                isMonitor: true,
                badgeDisplayMode,
                compact: true
            }),
        [data, badgeDisplayMode]
    );

    useEffect(() => {
        if (data?.icon_blink_interval > 0) {
            setBlink(true);
            const interval = setInterval(() => setBlink((prev) => !prev), data.icon_blink_interval);
            return () => clearInterval(interval);
        }
        setBlink(true);
        return undefined;
    }, [data?.icon_blink_interval]);

    const confirmationBadges = useMemo(() => {
        const raw =
            data?.confirmation_numbers ||
            (data?.confirmation_number
                ? [
                      {
                          confirmation_number: data.confirmation_number,
                          checkout_today: data.checkout_today || false,
                          checkin_today: data.checkin_today || false,
                          ...(data?.guest_last != null && data.guest_last !== '' ? { guest_last: data.guest_last } : {}),
                          ...(data?.guest_first != null && String(data.guest_first).trim() !== ''
                              ? { guest_first: data.guest_first }
                              : {})
                      }
                  ]
                : []);
        return sortConfirmationBadges(raw);
    }, [data]);

    const dayUseBadgePlan =
        confirmationBadges.length > 0
            ? getMonitorDayUseSplitBadgePlan(confirmationBadges, badgeDisplayMode)
            : null;

    const outerBorderPx = 3;
    const middleGapPx = 1;
    const innerBorderPx = 1;

    const rn = roomNumber != null ? String(roomNumber) : '';
    const numLen = rn.length;
    const roomFontSize = numLen > 3 ? 9 : numLen > 2 ? 10 : 11;
    const roomFontWeight = numLen > 4 ? 400 : 500;

    const effectiveFooterSlotPx =
        footerSlotHeightPx != null ? footerSlotHeightPx : layout.belowBlockHeightPx;
    const showFooterSlot =
        layout.belowBlockHeightPx > 0 ||
        layout.showMonitorTaskProgressBar ||
        layout.monitorHasBottomFooter ||
        Boolean(data?.label_1) ||
        effectiveFooterSlotPx > 0;

    return (
        <div
            className="compact-monitor-cell position-relative d-inline-flex flex-column align-items-center"
            style={{
                marginBottom: COMPACT_MONITOR_CELL_MARGIN_BOTTOM_PX,
                marginRight: 3,
                paddingTop: confirmationBadges.length > 0 ? COMPACT_MONITOR_DOT_PADDING_TOP_PX : 0,
                width: OUTER_DIAMETER,
                maxWidth: OUTER_DIAMETER
            }}
        >
            {confirmationBadges.length > 0 && (
                <div
                    className="position-absolute d-flex align-items-center justify-content-center gap-1"
                    style={{
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 2
                    }}
                >
                    {dayUseBadgePlan ? (
                        <button
                            type="button"
                            className="p-0 border-0"
                            aria-label={dayUseBadgePlan.tooltip}
                            title={dayUseBadgePlan.tooltip}
                            onClick={(e) => {
                                e.stopPropagation();
                                const confForClick =
                                    dayUseBadgePlan.kind === 'gradient_name'
                                        ? confirmationBadges[0]
                                        : dayUseBadgePlan.checkin.row;
                                onBadgeClick?.(confForClick);
                            }}
                            style={{
                                width: COMPACT_RESERVATION_DOT_SIZE,
                                height: COMPACT_RESERVATION_DOT_SIZE,
                                borderRadius: '50%',
                                background: MONITOR_BADGE_DAY_USE_GRADIENT_BG,
                                boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
                                cursor: 'pointer'
                            }}
                        />
                    ) : (
                        confirmationBadges.map((conf, index) => {
                            const { tooltip } = getMonitorBadgeLabel(conf, badgeDisplayMode);
                            return (
                                <button
                                    key={index}
                                    type="button"
                                    className="p-0 border-0"
                                    aria-label={tooltip}
                                    title={tooltip}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onBadgeClick?.(conf);
                                    }}
                                    style={{
                                        width: COMPACT_RESERVATION_DOT_SIZE,
                                        height: COMPACT_RESERVATION_DOT_SIZE,
                                        borderRadius: '50%',
                                        backgroundColor: dotColor(conf),
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.35)',
                                        cursor: 'pointer'
                                    }}
                                />
                            );
                        })
                    )}
                </div>
            )}

            <div
                ref={popperTargetRef}
                role={onCircleClick ? 'button' : 'img'}
                tabIndex={onCircleClick ? 0 : undefined}
                onClick={onCircleClick ? (e) => onCircleClick(e) : undefined}
                onKeyDown={
                    onCircleClick
                        ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  onCircleClick(e);
                              }
                          }
                        : undefined
                }
                aria-label={
                    onCircleClick
                        ? `Room ${roomNumber} — tap for full door view`
                        : `Room ${roomNumber}`
                }
                style={{ cursor: onCircleClick ? 'pointer' : 'default', lineHeight: 0 }}
            >
                {/* Layer 1: outer rim (status_display_outer_border_color) */}
                <div
                    style={{
                        width: OUTER_DIAMETER,
                        height: OUTER_DIAMETER,
                        borderRadius: '50%',
                        boxSizing: 'border-box',
                        border: `${outerBorderPx}px solid ${palette.outerBorder}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: middleGapPx,
                        backgroundColor: palette.ringColor
                    }}
                >
                    {/* Layer 2: inner disc — fill + inner border strip (matches door status box) */}
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            backgroundColor: palette.innerFill,
                            border: `${innerBorderPx}px solid ${palette.ringColor}`,
                            color: palette.roomNumberColor,
                            fontWeight: roomFontWeight,
                            fontSize: roomFontSize,
                            lineHeight: 1
                        }}
                    >
                        {roomNumber}
                    </div>
                </div>
            </div>

            {showFooterSlot ? (
                <div
                    className="compact-monitor-cell__below monitor-door-card-v2__below d-flex flex-column align-items-center"
                    style={
                        effectiveFooterSlotPx > 0
                            ? { minHeight: effectiveFooterSlotPx, width: '100%' }
                            : { width: '100%' }
                    }
                >
                    {layout.showMonitorTaskProgressBar && (
                        <div className="monitor-door-card-v2__progress px-1 compact-monitor-cell__progress">
                            <MiniProgressBar
                                timeStart={layout.progressTimingData.time_start}
                                functionSecondsAllocated={Number(
                                    layout.progressTimingData.function_seconds_allocated
                                )}
                                isDark
                                top={0}
                            />
                        </div>
                    )}

                    <MonitorCartInspectorFooterRow
                        layout={layout}
                        icon_blink_interval={data?.icon_blink_interval}
                        blink={blink}
                    />

                    {data?.label_1 && (
                        <div className="monitor-door-card-v2__label-row">
                            <span className="text-black fs-8 fw-medium text-uppercase text-center pt-1 w-100 d-block">
                                {data.label_1}
                            </span>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default CompactRoomMonitorCircle;
